import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

import {
  Checkbox,
  Input,
  InputGroup,
  InputGroupRadio,
  InputGroupSelectRange,
  InputMultiSelect,
  InputGroupYearRange
} from './common';
import { Button } from './common/buttons';

import AbilympicsProfessions from '../data/abilympicsProfession';
import {
  range,
  rangeGrouped,
  bestApprentice,
  genderOptions,
  chRangeGrouped,
  fullRangeGroup
} from '../data/professionOptions';
import { InputSelect } from './common';
import { RequiredIcon } from './icons';

const ouvrierOption = [
  {
    lable: 'Yes',
    value: 'yes'
  },
  {
    label: 'No',
    value: 'no'
  }
];

const GenericField = ({
  languages = [],
  fieldKey,
  isRequired,
  charsLimit,
  formik,
  regions,
  professions,
  listSwissRegions,
  listProfessionalAssoc,
  listSwissFunction,
  listSwissJobTitle,
  listVocationalSchool,
  regionalEvents,
  participantLocations,
  establishmentsTypes = [],
  orgPrefix
}) => {
  const { t } = useTranslation();

  const [updatedGenderOptions, setUpdatedGenderOptions] = useState(...genderOptions);
  const [updatedBestApprenticeOptions, setUpdatedBestApprenticeOptions] = useState([
    ...bestApprentice
  ]);
  const [updatedOuvrierOptions, setOuvrierOptions] = useState([...ouvrierOption]);
  const [fieldTranslation, setFieldTranslation] = useState({ label: '', placeholder: '' });
  const [rankFieldTranslation, setRankFieldTranslation] = useState({ label: '', placeholder: '' });
  // const [yearFieldTranslation, setYearFieldTranslation] = useState({ label: '', placeholder: '' });

  useEffect(() => {
    const tGenders = t('form.genderOptions', { returnObjects: true });

    setUpdatedGenderOptions([
      {
        label: tGenders['masculin']?.[orgPrefix] ?? tGenders['masculin']?.default,
        value: 'masculin'
      },
      {
        label: tGenders['féminin']?.[orgPrefix] ?? tGenders['féminin']?.default,
        value: 'féminin'
      }
    ]);

    const tBestApprentice = t('form.bestApprenticeOptions', { returnObjects: true });

    setUpdatedBestApprenticeOptions([
      {
        label: tBestApprentice['oui']?.[orgPrefix] ?? tBestApprentice['oui']?.default,
        value: 'oui'
      },
      {
        label: tBestApprentice['no']?.[orgPrefix] ?? tBestApprentice['no']?.default,
        value: 'no'
      }
    ]);

    const tOuvrier = t('form.ouvrierOptions', { returnObjects: true });

    setOuvrierOptions([
      {
        label: tOuvrier['yes']?.[orgPrefix] ?? tOuvrier['yes']?.default,
        value: 'yes'
      },
      {
        label: tOuvrier['no']?.[orgPrefix] ?? tOuvrier['no']?.default,
        value: 'no'
      }
    ]);

    const tFieldTranslation = t(`form.fields.${fieldKey}`, { returnObjects: true });
    setFieldTranslation(
      tFieldTranslation?.[orgPrefix]
        ? tFieldTranslation[orgPrefix]
        : tFieldTranslation?.default
        ? tFieldTranslation.default
        : { label: fieldKey, placeholder: '' }
    );

    const tRankFieldTranslation = t(`form.fields.rankField`, { returnObjects: true });
    setRankFieldTranslation(
      tRankFieldTranslation?.[orgPrefix]
        ? tRankFieldTranslation[orgPrefix]
        : tRankFieldTranslation.default
    );
  }, [fieldKey, orgPrefix]);

  const renderLabel = (label) => {
    return (
      <div className="flex items-center relative">
        {isRequired && <RequiredIcon className="text-red-500 text-xs absolute -left-4 top-1" />}
        <span dangerouslySetInnerHTML={{ __html: label }} />
      </div>
    );
  };

  return (
    <>
      {fieldKey === 'firstName' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="firstName"
          labelClassName="font-semibold mb-1"
          value={formik.values.firstName}
          touched={formik.touched.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.firstName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'familyName' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="familyName"
          labelClassName="font-semibold mb-1"
          value={formik.values.familyName}
          touched={formik.touched.familyName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.familyName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'birthYear' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="birthYear"
          type="number"
          labelClassName="font-semibold mb-1"
          value={formik.values.birthYear}
          touched={formik.touched.birthYear}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.birthYear}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'birthday' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="birthday"
          type="date"
          labelClassName={'font-semibold'}
          value={
            formik.values.birthday?.includes('T')
              ? formik.values.birthday.split('T')?.[0]
              : formik.values.birthday
          }
          touched={formik.touched.birthday}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.birthday}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'postalCode' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="postalCode"
          type="number"
          value={formik.values.postalCode}
          touched={formik.touched.postalCode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.postalCode}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'postcode' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="postcode"
          type="number"
          labelClassName="font-semibold mb-1"
          value={formik.values.postcode}
          touched={formik.touched.postcode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.postcode}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'city' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="city"
          labelClassName="font-semibold mb-1"
          value={formik.values.city}
          touched={formik.touched.city}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.city}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'abilympicsProfession' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="abilympicsProfession"
          labelClassName="font-semibold mb-1"
          value={formik.values.abilympicsProfession}
          touched={formik.touched.abilympicsProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.abilympicsProfession}
          options={AbilympicsProfessions}
          onChange={(val) => {
            formik.setFieldValue('abilympicsProfession', val, true);
            setTimeout(() => formik.setFieldTouched('abilympicsProfession', true));
          }}
        />
      )}
      {fieldKey === 'gender' && (
        <InputGroupRadio
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="gender"
          labelClassName="font-semibold mb-1"
          value={formik.values.gender}
          touched={formik.touched.gender}
          onBlur={formik.handleBlur}
          error={formik.errors.gender}
          setFieldValue={formik.setFieldValue}
          options={updatedGenderOptions}
        />
      )}
      {fieldKey === 'ouvrier' && (
        <InputGroupRadio
          label={renderLabel(fieldTranslation?.label)}
          labelClassName={'font-semibold'}
          className="my-1"
          name="ouvrier"
          value={formik.values.ouvrier}
          touched={formik.touched.ouvrier}
          onBlur={formik.handleBlur}
          error={formik.errors.ouvrier}
          setFieldValue={formik.setFieldValue}
          options={updatedOuvrierOptions}
        />
      )}
      {fieldKey === 'euroSkillsYear' && (
        <InputGroupSelectRange
          label={renderLabel(fieldTranslation?.label)}
          labelClassName={'font-semibold'}
          year={{
            name: 'euroSkillsYear',
            type: 'number',
            value: formik.values.euroSkillsYear,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: fieldTranslation?.placeholder,
            error: formik.errors.euroSkillsYear,
            touched: formik.touched.euroSkillsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            name: 'euroSkillsRank',
            // onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.euroSkillsRank,
            touched: formik.touched.euroSkillsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup,
            value: formik.values.euroSkillsRank
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('euroSkillsYear', '', false);
            formik.setFieldValue('euroSkillsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'worldSkillsYear' && (
        <InputGroupSelectRange
          label={renderLabel(fieldTranslation?.label)}
          labelClassName="font-semibold mb-1"
          year={{
            name: 'worldSkillsYear',
            type: 'number',
            value: formik.values.worldSkillsYear,
            placeholder: fieldTranslation?.placeholder,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.worldSkillsYear,
            touched: formik.touched.worldSkillsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            name: 'worldSkillsRank',
            touched: formik.touched.worldSkillsRank,
            value: formik.values.worldSkillsRank,
            onBlur: formik.handleBlur,
            error: formik.errors.worldSkillsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('worldSkillsYear', '', false);
            formik.setFieldValue('worldSkillsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'worldSkillsProfession' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="worldSkillsProfession"
          labelClassName={'font-semibold'}
          value={formik.values.worldSkillsProfession}
          touched={formik.touched.worldSkillsProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.worldSkillsProfession}
          options={professions}
          onChange={(val) => {
            console.log('onChange', val);
            formik.setFieldValue('worldSkillsProfession', val, true);
            setTimeout(() => formik.setFieldTouched('worldSkillsProfession', true));
          }}
        />
      )}
      {fieldKey === 'natFinalsRegion' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="natFinalsRegion"
          labelClassName="font-semibold mb-1"
          value={formik.values.natFinalsRegion}
          touched={formik.touched.natFinalsRegion}
          onBlur={formik.handleBlur}
          error={formik.errors.natFinalsRegion}
          options={regions}
          onChange={(val) => {
            formik.setFieldValue('natFinalsRegion', val, true);
            setTimeout(() => formik.setFieldTouched('natFinalsRegion', true));
          }}
        />
      )}
      {fieldKey === 'region' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="region"
          value={formik.values.region}
          touched={formik.touched.region}
          onBlur={formik.handleBlur}
          error={formik.errors.region}
          options={listSwissRegions}
          onChange={(val) => {
            formik.setFieldValue('region', val, true);
            setTimeout(() => formik.setFieldTouched('region', true));
          }}
        />
      )}
      {fieldKey === 'mobileNumber' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="mobileNumber"
          labelClassName={'font-semibold'}
          value={formik.values.mobileNumber}
          touched={formik.touched.mobileNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.mobileNumber}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'montherLanguage' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="montherLanguage"
          labelClassName={'font-semibold'}
          value={formik.values.montherLanguage}
          touched={formik.touched.montherLanguage}
          onBlur={formik.handleBlur}
          error={formik.errors.montherLanguage}
          options={languages.map((l) => ({ value: l, label: l?.toUpperCase() }))}
          onChange={(val) => {
            formik.setFieldValue('montherLanguage', val, true);
            setTimeout(() => formik.setFieldTouched('montherLanguage', true));
          }}
        />
      )}
      {fieldKey === 'otherLanguage' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="otherLanguage"
          labelClassName={'font-semibold'}
          value={formik.values.otherLanguage}
          touched={formik.touched.otherLanguage}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.otherLanguage}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'specialJobTitle' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="specialJobTitle"
          value={formik.values.specialJobTitle}
          touched={formik.touched.specialJobTitle}
          onBlur={formik.handleBlur}
          error={formik.errors.specialJobTitle}
          options={listSwissJobTitle}
          onChange={(val) => {
            formik.setFieldValue('specialJobTitle', val, true);
            setTimeout(() => formik.setFieldTouched('specialJobTitle', true));
          }}
        />
      )}
      {fieldKey === 'profession' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="profession"
          value={formik.values.profession}
          touched={formik.touched.profession}
          onBlur={formik.handleBlur}
          error={formik.errors.profession}
          options={professions}
          profiessionPlaceholder
          onChange={(val) => {
            formik.setFieldValue('profession', val, true);
            setTimeout(() => formik.setFieldTouched('profession', true));
          }}
        />
      )}
      {fieldKey === 'swissChampionYear' && (
        <InputGroupSelectRange
          label={renderLabel(fieldTranslation?.label)}
          labelClassName={'font-semibold'}
          setFieldValue={formik.setFieldValue}
          year={{
            name: 'swissChampionYear',
            type: 'number',
            value: formik.values.swissChampionYear,
            placeholder: fieldTranslation?.placeholder,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            error: formik.errors.swissChampionYear,
            touched: formik.touched.swissChampionYear
          }}
          range={{
            name: 'swissChampionRank',
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            type: 'select',
            // onChange: formik,
            onBlur: formik.handleBlur,
            error: formik.errors.swissChampionRank,
            value: formik.values.swissChampionRank,
            touched: formik.touched.swissChampionRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('swissChampionYear', '', false);
            formik.setFieldValue('swissChampionRank', '', false);
          }}
        />
      )}
      {fieldKey === 'currentCompanyCityDept' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="currentCompanyCityDept"
          labelClassName="font-semibold mb-1"
          value={formik.values.currentCompanyCityDept}
          touched={formik.touched.currentCompanyCityDept}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.currentCompanyCityDept}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'traningCopmanyCityDept' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="traningCopmanyCityDept"
          labelClassName="font-semibold mb-1"
          value={formik.values.traningCopmanyCityDept}
          touched={formik.touched.traningCopmanyCityDept}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.traningCopmanyCityDept}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'bestApprentice' && (
        <InputGroupRadio
          label={renderLabel(fieldTranslation?.label)}
          className="my-2"
          name="bestApprentice"
          labelClassName="font-semibold mb-1"
          value={formik.values.bestApprentice}
          touched={formik.touched.bestApprentice}
          // onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.bestApprentice}
          setFieldValue={formik.setFieldValue}
          options={updatedBestApprenticeOptions}
        />
      )}
      {fieldKey === 'bestWorker' && (
        <InputGroupRadio
          label={renderLabel(fieldTranslation?.label)}
          className="my-22"
          name="bestWorker"
          labelClassName="font-semibold mb-1"
          value={formik.values.bestWorker}
          touched={formik.touched.bestWorker}
          // onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.bestWorker}
          setFieldValue={formik.setFieldValue}
          options={bestApprentice}
        />
      )}
      {fieldKey === 'natFinalsYear' && (
        <InputGroupSelectRange
          label={renderLabel(fieldTranslation?.label)}
          labelClassName="font-semibold mb-1"
          year={{
            name: 'natFinalsYear',
            type: 'number',
            value: formik.values.natFinalsYear,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: fieldTranslation?.placeholder,
            error: formik.errors.natFinalsYear,
            touched: formik.touched.natFinalsYear
          }}
          range={{
            label: rankFieldTranslation?.label,
            placeholder: rankFieldTranslation?.placeholder,
            name: 'natFinalsRank',
            type: 'select',
            onBlur: formik.handleBlur,
            error: formik.errors.natFinalsRank,
            touched: formik.touched.natFinalsRank,
            setFieldValue: formik.setFieldValue,
            options: fullRangeGroup,
            value: formik.values.natFinalsRank
          }}
          labelFirst={true}
          onReset={() => {
            formik.setFieldValue('natFinalsYear', '', false);
            formik.setFieldValue('natFinalsRank', '', false);
          }}
        />
      )}
      {fieldKey === 'trainingAndDiploma' && (
        <InputGroup
          label={renderLabel(fieldTranslation?.label)}
          labelClassName="font-semibold mb-1"
          fields={[
            {
              name: 'trainingAndDiploma1',
              value: formik.values.trainingAndDiploma1,
              touched: formik.touched.trainingAndDiploma1,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma1,
              max: 90,
              placeholder: fieldTranslation?.placeholder
            },
            {
              name: 'trainingAndDiploma2',
              value: formik.values.trainingAndDiploma2,
              touched: formik.touched.trainingAndDiploma2,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma2,
              placeholder: fieldTranslation?.placeholder
            },
            {
              name: 'trainingAndDiploma3',
              value: formik.values.trainingAndDiploma3,
              touched: formik.touched.trainingAndDiploma3,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.trainingAndDiploma3,
              placeholder: fieldTranslation?.placeholder
            }
          ]}
        />
      )}
      {fieldKey === 'currentJobName' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="currentJobName"
          labelClassName="font-semibold mb-1"
          value={formik.values.currentJobName}
          touched={formik.touched.currentJobName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.currentJobName}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'linkedinAddress' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-1"
          name="linkedinAddress"
          labelClassName={'font-semibold'}
          value={formik.values.linkedinAddress}
          touched={formik.touched.linkedinAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.linkedinAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'instagramAddress' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="instagramAddress"
          labelClassName="font-semibold mb-1"
          value={formik.values.instagramAddress}
          touched={formik.touched.instagramAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.instagramAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'tiktokAddress' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="tiktokAddress"
          labelClassName="font-semibold mb-1"
          value={formik.values.tiktokAddress}
          touched={formik.touched.tiktokAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.tiktokAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'facebookAddress' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="facebookAddress"
          labelClassName="font-semibold mb-1"
          value={formik.values.facebookAddress}
          touched={formik.touched.facebookAddress}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.facebookAddress}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'associationProfession' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="associationProfession"
          value={formik.values.associationProfession}
          touched={formik.touched.associationProfession}
          onBlur={formik.handleBlur}
          error={formik.errors.associationProfession}
          options={listProfessionalAssoc}
          onChange={(val) => {
            formik.setFieldValue('associationProfession', val, true);
            setTimeout(() => formik.setFieldTouched('associationProfession', true));
          }}
        />
      )}
      {fieldKey === 'vocationSchool' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="vocationSchool"
          value={formik.values.vocationSchool}
          touched={formik.touched.vocationSchool}
          onBlur={formik.handleBlur}
          error={formik.errors.vocationSchool}
          options={listVocationalSchool}
          onChange={(val) => {
            formik.setFieldValue('vocationSchool', val, true);
            setTimeout(() => formik.setFieldTouched('vocationSchool', true));
          }}
        />
      )}
      {fieldKey === 'functions' && (
        <InputMultiSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="functions"
          value={formik.values.functions}
          touched={formik.touched.functions}
          onBlur={formik.handleBlur}
          error={formik.errors.functions}
          options={listSwissFunction}
          onChange={(val) => {
            formik.setFieldValue('functions', val, true);
            setTimeout(() => formik.setFieldTouched('functions', true));
          }}
        />
      )}
      {fieldKey === 'regionalEvents' && (
        <InputMultiSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="regionalEvents"
          value={formik.values.regionalEvents}
          touched={formik.touched.regionalEvents}
          onBlur={formik.handleBlur}
          error={formik.errors.regionalEvents}
          options={regionalEvents}
          onChange={(val) => {
            formik.setFieldValue('regionalEvents', val, true);
            setTimeout(() => formik.setFieldTouched('regionalEvents', true));
          }}
        />
      )}
      {fieldKey === 'participantLocation' && (
        <InputSelect
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          labelClassName={'font-semibold'}
          className="my-1"
          name="participantLocation"
          value={formik.values.participantLocation}
          touched={formik.touched.participantLocation}
          onBlur={formik.handleBlur}
          error={formik.errors.participantLocation}
          options={participantLocations}
          onChange={(val) => {
            formik.setFieldValue('participantLocation', val, true);
            setTimeout(() => formik.setFieldTouched('participantLocation', true));
          }}
        />
      )}
      {fieldKey === 'freeText' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="freeText"
          labelClassName="font-bold mb-1"
          value={formik.values.freeText}
          touched={formik.touched.freeText}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.freeText}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'freeText2' && (
        <Input
          label={renderLabel(fieldTranslation?.label)}
          placeholder={fieldTranslation?.placeholder}
          className="my-2"
          name="freeText2"
          labelClassName="font-bold mb-1"
          value={formik.values.freeText2}
          touched={formik.touched.freeText2}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.freeText2}
          limit={charsLimit}
        />
      )}
      {fieldKey === 'companyInformation' && (
        <>
          {formik.values?.companyInformation?.length > 0 && (
            <>
              {formik.values?.companyInformation?.map((_, index) => {
                const numberDisplay = index < 10 ? `0${index + 1}` : index + 1;

                return (
                  <div key={index} className="relative">
                    <InputGroup
                      label={renderLabel(
                        `${
                          numberDisplay && formik.values?.companyInformation?.length > 1
                            ? `[${numberDisplay}] `
                            : ''
                        }${fieldTranslation?.label}`
                      )}
                      labelClassName="font-semibold mb-1"
                      fields={[
                        {
                          name: `companyInformation[${index}].name`,
                          value: formik.values.companyInformation?.[index]?.name || '',
                          touched: formik.touched.companyInformation?.[index]?.name,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.name,
                          placeholder:
                            fieldTranslation?.name?.placeholder ||
                            `form.fields.${fieldKey}.name.placeholder`,
                          limit: charsLimit
                        },
                        {
                          name: `companyInformation[${index}].city`,
                          value: formik.values.companyInformation?.[index]?.city || '',
                          touched: formik.touched.companyInformation?.[index]?.city,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.city,
                          placeholder:
                            fieldTranslation?.city?.placeholder ||
                            `form.fields.${fieldKey}.city.placeholder`,
                          limit: charsLimit
                        },
                        {
                          name: `companyInformation[${index}].department`,
                          value: formik.values.companyInformation?.[index]?.department || '',
                          touched: formik.touched.companyInformation?.[index]?.department,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.companyInformation?.[index]?.department,
                          placeholder:
                            fieldTranslation?.department?.placeholder ||
                            `form.fields.${fieldKey}.department.placeholder`,
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `companyInformation[${index}].current`,
                          label:
                            fieldTranslation?.current?.label ||
                            `form.fields.${fieldKey}.current.label`,
                          checked: formik.values.companyInformation?.[index]?.current || false,
                          onChange: () => {
                            // Update the rest to false and this one to true only
                            formik.values?.companyInformation?.forEach((item, i) => {
                              if (i !== index) {
                                formik.setFieldValue(`companyInformation[${i}].current`, false);
                              }
                            });
                            formik.setFieldValue(`companyInformation[${index}].current`, true);
                          },
                          touched: formik.touched.companyInformation?.[index]?.current,
                          error: formik.errors.companyInformation?.[index]?.current,
                          inOneLine: true,
                          limit: charsLimit
                        }
                      ]}
                    />

                    {formik.values?.companyInformation?.length > 1 && (
                      <button
                        type="button"
                        className="cursor-pointer text-red-500 absolute top-3 right-0"
                        onClick={() => {
                          const newCompanyInformation = formik.values.companyInformation.filter(
                            (_, i) => i !== index
                          );
                          formik.setFieldValue('companyInformation', newCompanyInformation);
                        }}>
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {formik.values?.companyInformation?.length < 2 && (
            <Button
              type="button"
              icon={PlusIcon}
              onClick={() => {
                formik.setFieldValue('companyInformation', [
                  ...(formik.values.companyInformation || []),
                  {}
                ]);
              }}>
              <PlusIcon className="w-5 h-5" />{' '}
              {fieldTranslation?.addCompany?.label || `form.fields.${fieldKey}.addCompany.label`}
            </Button>
          )}
        </>
      )}
      {fieldKey === 'educationInformation' && (
        <>
          {formik.values?.educationInformation?.length > 0 && (
            <>
              {formik.values?.educationInformation?.map((_, index) => {
                const numberDisplay = index < 10 ? `0${index + 1}` : index + 1;

                return (
                  <div key={index} className="relative">
                    <InputGroup
                      label={renderLabel(
                        `${
                          numberDisplay && formik.values?.educationInformation?.length > 1
                            ? `[${numberDisplay}] `
                            : ''
                        }${fieldTranslation?.label}`
                      )}
                      labelClassName="font-semibold mb-1"
                      fields={[
                        {
                          type: 'select',
                          name: `educationInformation[${index}].typeOfEstablishment`,
                          placeholder:
                            fieldTranslation?.typeOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.typeOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value: formik.values.educationInformation?.[index]?.typeOfEstablishment
                            ? {
                                value:
                                  formik.values.educationInformation?.[index]?.typeOfEstablishment,
                                label:
                                  formik.values.educationInformation?.[index]?.typeOfEstablishment
                              }
                            : null,
                          touched:
                            formik.touched.educationInformation?.[index]?.typeOfEstablishment,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.typeOfEstablishment,
                          options: establishmentsTypes?.map((type) => ({
                            value: type,
                            label: type
                          })),
                          onChange: (val) => {
                            formik.setFieldValue(
                              `educationInformation[${index}].typeOfEstablishment`,
                              val?.value || val,
                              true
                            );
                            setTimeout(() =>
                              formik.setFieldTouched(
                                `educationInformation[${index}].typeOfEstablishment`,
                                true
                              )
                            );
                          },
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].nameOfEstablishment`,
                          placeholder:
                            fieldTranslation?.nameOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.nameOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]?.nameOfEstablishment || '',
                          touched:
                            formik.touched.educationInformation?.[index]?.nameOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.nameOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].cityOfEstablishment`,
                          placeholder:
                            fieldTranslation?.cityOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.cityOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]?.cityOfEstablishment || '',
                          touched:
                            formik.touched.educationInformation?.[index]?.cityOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.cityOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].departmentOfEstablishment`,
                          placeholder:
                            fieldTranslation?.departmentOfEstablishment?.placeholder ||
                            `form.fields.${fieldKey}.departmentOfEstablishment.placeholder`,
                          labelClassName: 'font-semibold',
                          value:
                            formik.values.educationInformation?.[index]
                              ?.departmentOfEstablishment || '',
                          touched:
                            formik.touched.educationInformation?.[index]?.departmentOfEstablishment,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error:
                            formik.errors.educationInformation?.[index]?.departmentOfEstablishment,
                          limit: charsLimit
                        },
                        {
                          name: `educationInformation[${index}].diplomaTitle`,
                          placeholder:
                            fieldTranslation?.diplomaTitle?.placeholder ||
                            `form.fields.${fieldKey}.diplomaTitle.placeholder`,
                          labelClassName: 'font-semibold',
                          value: formik.values.educationInformation?.[index]?.diplomaTitle || '',
                          touched: formik.touched.educationInformation?.[index]?.diplomaTitle,
                          onChange: formik.handleChange,
                          onBlur: formik.handleBlur,
                          error: formik.errors.educationInformation?.[index]?.diplomaTitle,
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `educationInformation[${index}].diplomaCompleted`,
                          label:
                            fieldTranslation?.diplomaCompleted?.label ||
                            `form.fields.${fieldKey}.diplomaCompleted.label`,
                          checked:
                            formik.values.educationInformation?.[index]?.diplomaCompleted || false,
                          onChange: () => {
                            const currentValue =
                              formik.values.educationInformation?.[index]?.diplomaCompleted ||
                              false;
                            formik.setFieldValue(
                              `educationInformation[${index}].diplomaCompleted`,
                              !currentValue
                            );
                          },
                          touched: formik.touched.educationInformation?.[index]?.diplomaCompleted,
                          error: formik.errors.educationInformation?.[index]?.diplomaCompleted,
                          inOneLine: true,
                          limit: charsLimit
                        },
                        {
                          type: 'checkbox',
                          name: `educationInformation[${index}].current`,
                          label:
                            fieldTranslation?.current?.label ||
                            `form.fields.${fieldKey}.current.label`,
                          checked: formik.values.educationInformation?.[index]?.current || false,
                          onChange: () => {
                            // Update the rest to false and this one to true only
                            // formik.values?.educationInformation?.forEach((item, i) => {
                            //   if (i !== index) {
                            //     formik.setFieldValue(`educationInformation[${i}].current`, false);
                            //   }
                            // });
                            formik.setFieldValue(
                              `educationInformation[${index}].current`,
                              !formik.values.educationInformation?.[index]?.current || false
                            );
                          },
                          touched: formik.touched.educationInformation?.[index]?.current,
                          error: formik.errors.educationInformation?.[index]?.current,
                          inOneLine: true,
                          limit: charsLimit
                        }
                      ]}
                    />

                    {formik.values?.educationInformation?.length > 1 && (
                      <button
                        type="button"
                        className="cursor-pointer text-red-500 absolute top-3 right-0"
                        onClick={() => {
                          const newEducationInformation = formik.values.educationInformation.filter(
                            (_, i) => i !== index
                          );
                          formik.setFieldValue('educationInformation', newEducationInformation);
                        }}>
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          <Button
            type="button"
            icon={PlusIcon}
            onClick={() => {
              formik.setFieldValue('educationInformation', [
                ...(formik.values.educationInformation || []),
                {}
              ]);
            }}>
            <PlusIcon className="w-5 h-5" />{' '}
            {fieldTranslation?.addEducation?.label || `form.fields.${fieldKey}.addEducation.label`}
          </Button>
        </>
      )}
      {fieldKey === 'recentProfession' && (
        <InputGroup
          label={renderLabel(fieldTranslation?.label)}
          labelClassName={'font-semibold'}
          fields={[
            {
              name: 'recentProfession1',
              value: formik.values.recentProfession1,
              touched: formik.touched.recentProfession1,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession1,
              max: 90,
              placeholder: fieldTranslation?.placeholder
            },
            {
              name: 'recentProfession2',
              value: formik.values.recentProfession2,
              touched: formik.touched.recentProfession2,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession2,
              placeholder: fieldTranslation?.placeholder
            },
            {
              name: 'recentProfession3',
              value: formik.values.recentProfession3,
              touched: formik.touched.recentProfession3,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              error: formik.errors.recentProfession3,
              placeholder: fieldTranslation?.placeholder
            }
          ]}
        />
      )}
      {fieldKey === 'participationType' && (
        <InputGroupRadio
          label={renderLabel(fieldTranslation?.label)}
          // description={fieldTranslation.label}
          labelClassName={'font-semibold'}
          className="my-1"
          name="participationType"
          value={formik.values.participationType}
          touched={formik.touched.participationType}
          onBlur={formik.handleBlur}
          error={formik.errors.participationType}
          setFieldValue={formik.setFieldValue}
          // Special case for translation
          options={t('form.participationType.values', { returnObjects: true })}
          optionDirection="col"
        />
      )}
      {fieldKey === 'consent' && (
        <Checkbox
          label={renderLabel(fieldTranslation?.label)}
          name="consent"
          className="checked:accent-chred-500 !w-6 !h-6 rounded-full mr-5 "
          enabled={formik.values.consent}
          onChange={() => formik.setFieldValue('consent', !formik.values.consent)}
          touched={formik.touched.consent}
          error={formik.errors.consent}
          inOneLine={true}
        />
      )}
    </>
  );
};

export default GenericField;
